import { FormError } from '@/constant';
import { CommonActions } from '@/stores';
import { showAlert, showToast } from '@/utils';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { FormikProps } from 'formik';

export const useLogic = (
	formik: FormikProps<{ [key: string]: any }>,
	fieldName: string = 'email'
) => {
	const { t } = useTranslation(['common', 'validation']);

	const dispatch = useDispatch();

	const handleEmailChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			formik.setValues((pre: { [key: string]: any }) => ({
				...pre,
				[fieldName]: e.target.value,
				isVerifyEmail: false, // Reset check state to false when email changes.
				isValidEmail: false, // Reset check state to false when email changes.
			}));
		},
		[formik]
	);

	const verifyEmail = useCallback(async () => {
		if (formik.errors[fieldName] === FormError.REQUIRED) {
			formik.setFieldTouched(fieldName, true);
			return showAlert(t('validation:ALERT_12'));
		}
		if (formik.errors[fieldName] === FormError.FORMAT) {
			formik.setFieldTouched(fieldName, true);
			return showAlert(t('validation:ALERT_13'));
		}
		dispatch(
			CommonActions.verifyEmailRequest({
				email: formik.values[fieldName]?.trim(),
				callback: (params) => {
					if (params?.error) {
						formik.setValues((pre: { [key: string]: any }) => ({
							...pre,
							isValidEmail: false,
							isVerifyEmail: true,
						}));
						showToast(t('validation:DUPLICATE_EMAIL_DOMAIN'), {
							type: 'ERROR',
						});

						return;
					}

					formik.setValues((pre: { [key: string]: any }) => ({
						...pre,
						isValidEmail: true,
						isVerifyEmail: true,
					}));
					formik.setFieldTouched(fieldName, false);
					showToast(t('validation:AVAILABLE_EMAIL_DOMAIN'));
				},
			})
		);
	}, [formik, t]);

	const isAvailableEmail = useMemo(() => {
		const isVerifiedEmail = formik?.values?.isVerifyEmail;
		const isValid = formik?.values?.isValidEmail;

		if (!isVerifiedEmail) return undefined;
		return isValid;
	}, [formik?.values?.isValidEmail, formik?.values?.isVerifyEmail]);

	return {
		t,
		verifyEmail,
		handleEmailChange,
		isAvailableEmail,
	};
};
